/**
 * OP Sync Dashboard Page
 *
 * Compact page shell: entity selector + mode tabs.
 * All view logic lives in AuditView and FullRunsView.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchOPSyncDashboard } from '@/lib/api';
import { EMPTY_ENTITY_STATS } from '@/components/op-sync/shared';
import { AuditView } from '@/components/op-sync/AuditView';
import { FullRunsView } from '@/components/op-sync/FullRunsView';

export default function OpSyncPage() {
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);

  const dashboardQuery = useQuery({
    queryKey: ['op-sync', 'dashboard'],
    queryFn: fetchOPSyncDashboard,
    refetchInterval: 10000,
  });

  const stats = dashboardQuery.data?.stats || [];
  const configuredEntities = dashboardQuery.data?.entities || [];

  const statsByEntity = useMemo(
    () => new Map(stats.map((s) => [s.entityType, s])),
    [stats],
  );

  const entities = useMemo(() => {
    const ordered =
      configuredEntities.length > 0
        ? configuredEntities
        : stats.map((s) => s.entityType);
    return ordered.map(
      (e) => statsByEntity.get(e) ?? { ...EMPTY_ENTITY_STATS, entityType: e },
    );
  }, [configuredEntities, stats, statsByEntity]);

  useEffect(() => {
    if (!selectedEntity && entities.length > 0) {
      setSelectedEntity(entities[0].entityType);
    }
  }, [entities, selectedEntity]);

  const selectedStats = selectedEntity
    ? statsByEntity.get(selectedEntity) ?? {
        ...EMPTY_ENTITY_STATS,
        entityType: selectedEntity,
      }
    : null;

  /* ---- Loading skeleton ---- */
  if (dashboardQuery.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-7 w-32" />
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-9 w-24 rounded-md" />
          ))}
        </div>
        <Skeleton className="h-10 w-48 rounded-md" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  /* ---- Empty state ---- */
  if (entities.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">OP Sync</h1>
        <div className="mt-6 rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No entities configured. Add an entity in the OP Sync config to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">OP Sync</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Compare Dataverse records against Open Publishing to find field
          discrepancies
        </p>
      </div>

      {/* Entity selector: compact pill buttons */}
      <div className="flex flex-wrap gap-2">
        {entities.map((entity) => (
          <Button
            key={entity.entityType}
            size="sm"
            variant={
              selectedEntity === entity.entityType ? 'default' : 'outline'
            }
            onClick={() => setSelectedEntity(entity.entityType)}
            className="gap-1.5"
          >
            {entity.entityType}
            {entity.deadLetter > 0 && (
              <Badge
                variant="destructive"
                className="ml-1 h-4 min-w-4 px-1 text-[10px]"
              >
                {entity.deadLetter}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Mode tabs */}
      {selectedEntity && selectedStats && (
        <Tabs defaultValue="audit">
          <TabsList>
            <TabsTrigger value="audit">Audit</TabsTrigger>
            <TabsTrigger value="full-runs">Full Runs</TabsTrigger>
          </TabsList>
          <TabsContent value="audit">
            <AuditView entityType={selectedEntity} />
          </TabsContent>
          <TabsContent value="full-runs">
            <FullRunsView entityType={selectedEntity} stats={selectedStats} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
